import React from 'react';
import { Box, Button, Grid2 as Grid, TextField, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { EnumDropdown, SelectInput, FormFieldsData } from 'framework/form';
import { RuleValidationResult, TargetRule } from '../types';
import { techSpecializations } from '../services/validationRuleService';
import { PositionType } from 'domain/shared/employee';
import { EMPLOYEE_COMPETENCY_LEVEL } from 'domain/shared/performanceMeasurement';
import type { Technology } from 'domain/shared/career';

interface RuleFormProps {
  rule: Omit<TargetRule, 'id'>;
  validation: RuleValidationResult;
  onChange: (field: string, value: any) => void;
  onSubmit?: () => void;
  submitLabel?: string;
  disabled?: boolean;
  positions: PositionType[];
  technologies: Technology[];
  showSubmitButton?: boolean;
  isInDialog?: boolean;
}

const RuleForm: React.FC<RuleFormProps> = ({
  rule,
  validation,
  onChange,
  onSubmit,
  submitLabel = 'Add',
  disabled = false,
  positions,
  technologies,
  showSubmitButton = true,
  isInDialog = false,
}) => {
  const showTechnology = positions.some(
    element => techSpecializations.includes(element.name) && element.id === rule.positionId,
  );

  const handleSpecializationChange = (values: any) => {
    const remainSpecialisation =
      values.positionId &&
      positions.some(element => techSpecializations.includes(element.name) && element.id === values.positionId);
    if (!remainSpecialisation) {
      onChange('technologyId', undefined);
    }
    onChange('positionId', values.positionId);
  };

  const capacityValue = rule.capacity !== undefined ? rule.capacity : 0;

  return (
    <Grid container spacing={isInDialog ? 1 : 2} alignItems="start" data-testid="rule-form">
      <Grid size={isInDialog ? { xs: 6 } : { xs: 12, sm: 2 }}>
        <TextField
          id="capacity"
          data-testid="rule-form-capacity"
          hiddenLabel
          type="number"
          fullWidth
          value={capacityValue}
          placeholder="Add amount"
          sx={{ '.MuiInputBase-input': { padding: '9px 10px' } }}
          onChange={e => onChange('capacity', Number(e.target.value) || 0)}
          slotProps={{
            htmlInput: {
              min: 1,
              max: validation.availableCount,
            },
          }}
          error={!validation.valid}
          helperText={!validation.valid ? validation.message : ''}
          disabled={disabled}
        />
      </Grid>

      <Grid size={isInDialog ? { xs: 6 } : { xs: 12, sm: 2 }}>
        <EnumDropdown
          data-testid="rule-form-competency-level"
          sx={{ fontSize: 12 }}
          source="competencyLevel"
          options={EMPLOYEE_COMPETENCY_LEVEL}
          value={rule.competencyLevel}
          label=""
          placeholder="Select level"
          onChangeCallback={(values: FormFieldsData) => onChange('competencyLevel', values.competencyLevel)}
          disabled={disabled}
        />
      </Grid>

      <Grid size={isInDialog ? { xs: 6 } : { xs: 12, sm: 3 }}>
        <SelectInput
          menuPlacement="auto"
          source="positionId"
          label=""
          value={rule.positionId}
          placeholder="Select Position"
          options={positions.map(spec => ({ label: spec.name, value: spec.id }))}
          onChangeCallback={handleSpecializationChange}
          disabled={disabled}
        />
      </Grid>

      <Grid size={isInDialog ? { xs: 6 } : { xs: 12, sm: 3 }}>
        {showTechnology && (
          <SelectInput
            placeholder="Select technology"
            menuPlacement="auto"
            source="technologyId"
            label=""
            value={rule.technologyId}
            onChangeCallback={values => onChange('technologyId', values.technologyId)}
            disabled={disabled}
            options={technologies.map(tech => ({ label: tech.name, value: tech.id }))}
          />
        )}
      </Grid>

      <Grid size={{ xs: 12, sm: 2 }}>
        {showSubmitButton && !isInDialog && (
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'end' }}>
            <Tooltip title={validation.valid ? `${submitLabel} qualification rule` : validation.message || ''}>
              <span>
                <Button
                  data-testid="submit-new-rule"
                  variant="contained"
                  color="primary"
                  onClick={onSubmit}
                  startIcon={<AddIcon />}
                  disabled={!validation.valid || disabled || rule.capacity! < 1}
                  fullWidth
                >
                  {submitLabel}
                </Button>
              </span>
            </Tooltip>
          </Box>
        )}
      </Grid>
    </Grid>
  );
};

export default RuleForm;
